function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { Col, Row, Collapse, Button } from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash';
import Papa from 'papaparse';
import { getContentValue } from '@truqc/content-path';
import autoMatchCSVColumns from './csvAutoMatch';
import Label from './label';
import formActionCreators from '../../../actions/formActionCreators';
import store from '../../../reducers/storeRegistration';
import config from '../../../config.json';
import entityComposition from '../../common/classes/entityComposition';
const rejectedSingleMessage = 'Only individual .CSV or .csv files are allowed, please retry your upload with a SINGLE valid file.';
const actionCreators = {
  formActionCreators // only form currently supports the editor cache
};
const {
  env: buildEnvironment
} = config;
const validateHeaders = headers => {
  const validHeaders = [];
  const headerValidationWarnings = [];
  const singularHeaders = headers.filter(header => !header.includes('_'));
  headers.forEach(header => {
    // validate dupe headers
    if (header.includes('_') && header.split('_').length === 2) {
      const prefix = header.split('_')[0];
      const suffix = header.split('_')[1];
      if (singularHeaders.includes(prefix)) {
        const suffixNum = Math.floor(Number(suffix));
        if (suffixNum !== Infinity && String(suffixNum) === suffix && suffixNum >= 0) {
          headerValidationWarnings.push({
            rowIndex: 'Header',
            errorType: `Looks like there's more then one column named '${prefix}'`,
            actionTaken: `'_${suffix}' appended to extra column name`,
            message: `Consider renaming one of your '${prefix}' to something unique`
          });
        }
      }
    }
  });
  let missingColumnNames = 0;
  headers.forEach(header => {
    // validate empty string headers
    if (header === '') {
      missingColumnNames += 1;
    } else {
      validHeaders.push(header);
    }
  });
  if (missingColumnNames) {
    headerValidationWarnings.push({
      rowIndex: 'Header',
      errorType: 'Missing column name',
      actionTaken: `${missingColumnNames} column(s) will be ignored on import`,
      message: 'Give columns unique identifiers'
    });
  }
  return {
    headerValidationWarnings,
    validHeaders
  };
};
const extraRowColumnsParseKey = '__parsed_extra';
const SelectAndParseCSV = ({
  instructions: _instructions = null,
  title: _title = 'Upload CSV',
  onChange,
  contentPath,
  cachedVariableName,
  fileStatsContentPath: _fileStatsContentPath = null,
  reduxEntity,
  notificationActions
}) => {
  // console.log('props', props);
  // State to store table Column name
  const [csvColumnHeaders, setcsvColumnHeaders] = useState([]);
  // State to control debug display
  const [collapseRawData, setCollapseRawData] = useState(false);
  // State to store the values
  const [values, setValues] = useState([]);
  const [csvValidationErrors, setCSVValidationErrors] = useState([]);
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    const tmpFile = acceptedFiles[0];
    const parseErrors = [];

    // with the rejection handled below, this case will only get hit if there are
    // A: more then 1 csv file
    // B: more then 1 csv file AND at least 1 additional non csv file
    if (acceptedFiles.length > 1 || rejectedFiles.length) {
      notificationActions.error({
        message: rejectedSingleMessage
      });
      return;
    }
    if (tmpFile === undefined) return;
    await Papa.parse(tmpFile, {
      header: true,
      skipEmptyLines: true,
      complete: async ({
        data
      }) => {
        const headers = Object.keys(data[0]);
        const {
          headerValidationWarnings,
          validHeaders
        } = validateHeaders(headers);
        parseErrors.push(...headerValidationWarnings);

        // Filtered Column Names
        setcsvColumnHeaders(validHeaders);
        const headerObjects = {};
        validHeaders.forEach((head, index) => {
          headerObjects[camelCase(head)] = {
            title: head,
            _created: index,
            contentPath: null
          };
        });

        // Auto-match CSV columns to form fields using similarity matching
        const state = store.getState();
        const formState = state.form || {};
        const activeForm = formState.active || {};
        const formContent = activeForm.content || {};
        const formContentPath = getContentValue('/formContentPath', formContent);

        // Apply auto-matching with similarity threshold of 0.8 (80%)
        const matchedHeaderObjects = autoMatchCSVColumns(headerObjects, formContentPath, 0.8);
        const parsedData = [];
        data.forEach((row, index) => {
          const {
              [extraRowColumnsParseKey]: extraColumns,
              '': emptyColumn // remove empty columns
            } = row,
            rest = _objectWithoutProperties(row, [extraRowColumnsParseKey, ""].map(_toPropertyKey));
          if (extraColumns) {
            parseErrors.push({
              rest,
              rowIndex: index + 1,
              // +1 for human readable
              extraColumns,
              errorType: 'Too many columns for row.',
              actionTaken: 'Extra columns ignored.'
            });
          }
          parsedData.push(Object.values(rest));
        });
        setValues(parsedData);
        await onChange({
          path: contentPath,
          value: matchedHeaderObjects,
          _created: new Date().toISOString()
        });
        await onChange({
          path: _fileStatsContentPath,
          value: tmpFile.name
        });

        /*  Note - we're using a local client cache for data that we don't want to pass between
        *         client and server (could use 'transientContext' for that), and that we do NOT
        *         want to persist beyond the session (could use 'content' for that)
        */
        const entityActions = actionCreators[`${reduxEntity}ActionCreators`];
        await store.dispatch(entityActions.setEditorCacheValue(cachedVariableName, data));
        setCSVValidationErrors(parseErrors);
      }
    });
  }, [contentPath, cachedVariableName, onChange, _fileStatsContentPath, reduxEntity, notificationActions]);
  let columnMaxLength = 10;
  csvColumnHeaders.forEach(val => {
    if (val.length > columnMaxLength) columnMaxLength = val.length;
  });
  columnMaxLength += 2;
  const toggleShowRawData = () => setCollapseRawData(!collapseRawData);
  const validationErrorSection = csvValidationErrors.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    value: "CSV Parsing Errors"
  }), /*#__PURE__*/React.createElement("ul", null, csvValidationErrors.map(({
    rowIndex,
    extraColumns: _extraColumns = [],
    errorType,
    actionTaken,
    message
  }) => /*#__PURE__*/React.createElement("li", {
    key: rowIndex + uuidV4()
  }, /*#__PURE__*/React.createElement("pre", null, [`Problematic Row:  ${rowIndex}`, `Error Type:       ${errorType}`, `Action Taken:     ${actionTaken}`, message ? `Message:          ${message}` : null].join('\n')), _extraColumns.length ? /*#__PURE__*/React.createElement("pre", null, _extraColumns.map(entity => `❌ Extra Column: '${entity}'`).join('\n')) : null)))) : null;

  // note - we could set this up for truqc admin users as well - detect the username also show
  const debugData = buildEnvironment !== 'beta' && buildEnvironment !== 'production' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Label, {
    value: "CSV Uploaded and Parsed"
  }), /*#__PURE__*/React.createElement(Button, {
    "data-testid": "debugDataButton",
    onClick: toggleShowRawData
  }, collapseRawData ? 'Hide' : 'Show', " parsed document"), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("small", null, "Note that this data is only available on inital upload, not subsequent detail views"), /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Collapse, {
    isOpen: collapseRawData
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    sm: 12
  }, /*#__PURE__*/React.createElement("h4", null, "Validated Column Headers: ", csvColumnHeaders.length, /*#__PURE__*/React.createElement("br", null)), /*#__PURE__*/React.createElement("h4", null, "Rows found: ", values.length), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("h3", null, "Validated Data"), /*#__PURE__*/React.createElement("ul", null, values.map(vals => /*#__PURE__*/React.createElement("li", {
    key: uuidV4()
  }, /*#__PURE__*/React.createElement("pre", null, vals && vals.length ? vals.map((v, i) => `${csvColumnHeaders[i]}:${Array(columnMaxLength - csvColumnHeaders[i].length).fill(' ').join('')}${v}`).join('\n') : null)))), /*#__PURE__*/React.createElement("hr", null)))), validationErrorSection) : validationErrorSection;
  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    }
  });
  return csvColumnHeaders && csvColumnHeaders.length ? debugData : /*#__PURE__*/React.createElement("div", {
    className: "t-dud-dropzone"
  }, /*#__PURE__*/React.createElement(Label, {
    value: _title
  }), /*#__PURE__*/React.createElement("div", _objectSpread(_objectSpread({}, getRootProps()), {}, {
    className: "dropzone t-u-dropzone"
  }), /*#__PURE__*/React.createElement("input", _objectSpread({
    alt: "file-input",
    "data-testid": "drop-input"
  }, getInputProps())), _instructions || 'Drag a single csv file or click here to upload a single csv document to parse.'));
};
SelectAndParseCSV.propTypes = {
  title: PropTypes.string,
  reduxEntity: PropTypes.string.isRequired,
  instructions: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  fileStatsContentPath: PropTypes.string,
  contentPath: PropTypes.string.isRequired,
  cachedVariableName: PropTypes.string.isRequired,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired
};
export { SelectAndParseCSV as TestSelectAndParseCSV };
export default entityComposition(SelectAndParseCSV, [], {}, ['notification']);